import { useAuth } from "@/hooks/useAuth";
import { usePassRedeemStorage } from "@/hooks/usePassRedeemStorage";
import { fetchPassRedeem } from "@/services/passredeeem.queries";
import { useToastNotify } from "@/stores/toastStore";
import { useQuery } from "@tanstack/react-query";
import { t } from "@transifex/native";
import { useFocusEffect } from "expo-router";
import { PassRedeemFailReason } from "ipctg-inflow-types/InFlowEnums";
import { useCallback, useEffect, useState } from "react";
import { useAccount } from "./useAccount";

export function usePassRedeem(enabled: boolean, passID: string, termsAndConditionsOKTimestamp: string) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { account, isFetching: isAccountFetching } = useAccount();
  const { notify } = useToastNotify();
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [errorNotified, setErrorNotified] = useState(false);

  const { alreadyRedeemed, hasLoaded, lastPassRedeem, saveLastPassRedeem } = usePassRedeemStorage(termsAndConditionsOKTimestamp);

  useEffect(() => {
    if (account?.phone) {
      setPhoneNumber(account.phone);
    }
  }, [account]);

  const { data, isPending, error } = useQuery({
    enabled: enabled && hasLoaded && !alreadyRedeemed && !!passID && !isAuthLoading && !isAccountFetching,
    queryKey: ["pass-redeem", passID, termsAndConditionsOKTimestamp, phoneNumber],
    queryFn: () =>
      fetchPassRedeem({
        cognitoIDToken: user?.idToken ?? "",
        passID,
        termsAndConditionsOKTimestamp,
        phoneNumber: phoneNumber,
      }),
    retry: false,
  });

  useFocusEffect(
    useCallback(() => {
      if (data) {
        saveLastPassRedeem({
          passRedeemResponse: data,
          termsAndConditionsOKTimestamp,
        });
      }
    }, [data, saveLastPassRedeem, termsAndConditionsOKTimestamp]),
  );

  useEffect(() => {
    if (error && !errorNotified) {
      if ("failReason" in error) {
        switch (error.failReason) {
          case PassRedeemFailReason.SessionEnforced:
          case PassRedeemFailReason.SessionInViolation:
            notify({
              severity: "error",
              message: t("This plate is currently in violation status. Parking activation is no longer available."),
            });
            break;
          default:
            notify({
              severity: "error",
              message: t("An error occurred when fetching checkout data"),
            });
            break;
        }
      } else {
        notify({
          severity: "error",
          message: t("An error occurred when fetching checkout data"),
        });
      }
      console.error("Error redeeming pass:", error);
      setErrorNotified(true);
    }
    if (!error) {
      setErrorNotified(false);
    }
  }, [error, errorNotified, notify]);

  return {
    data: alreadyRedeemed ? lastPassRedeem?.passRedeemResponse : data,
    isPending: !(hasLoaded || !isPending),
    phoneNumber,
  };
}
